import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { CreditUsageBar } from '@/components/ui/credit-usage-bar';
import { CreditTransactionHistory } from './CreditTransactionHistory';
import { CreditCostCalculator } from './CreditCostCalculator';
import { CreditStructure } from './CreditStructure';
import { CreditUsageAnalytics } from './CreditUsageAnalytics';
import { useCredits } from '@/hooks/useCredits';
import { Zap, Calculator, History, TrendingUp, BarChart3, Info, Sparkles, DollarSign } from 'lucide-react';

export function Credits() {
  const { balance, usage, loading, error } = useCredits();
  const [activeTab, setActiveTab] = useState('history');

  // Debug logging
  useEffect(() => {
    console.log('[Credits Page] Data state:', {
      balance,
      usage,
      loading,
      error
    });
  }, [balance, usage, loading, error]);

  return (
    <div className="flex flex-col min-w-0 space-y-6">
      {/* Enhanced Page Header */}
      <div className="px-4 sm:px-6 lg:px-8 pt-8">
        <div className="relative overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/40 rounded-2xl border border-purple-200/50 shadow-sm p-8">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-indigo-200/20 to-transparent rounded-full translate-y-16 -translate-x-16" />
          <div className="relative">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <Zap className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 bg-clip-text text-transparent mb-2">
                  Credits Management
                </h1>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Track your credit usage, view transaction history, and estimate costs for your content creation needs with comprehensive analytics and insights.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Credit Scope Information */}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950/20 dark:via-indigo-950/20 dark:to-purple-950/20 rounded-xl border border-blue-200/50 dark:border-blue-800/50 p-4">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 to-purple-100/20 dark:from-blue-900/10 dark:to-purple-900/10" />
          <div className="relative flex items-start gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Personal Account Credits
              </h3>
              <p className="text-blue-800 dark:text-blue-200 text-sm leading-relaxed">
                The credits and transactions shown here are for your personal account only. 
                Team and organization accounts that you've been invited to have separate credit systems managed independently by their owners. 
                Each user manages their own credits across all accounts they access.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Credit Balance Overview */}
      <div className="px-4 sm:px-6 lg:px-8">
        <Card className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
          <div className="p-6 border-b bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100 mb-1">
                  Current Balance
                </h3>
                <p className="text-sm text-emerald-700 dark:text-emerald-200">
                  Your available credits and monthly usage overview
                </p>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <CreditUsageBar 
                  showDetails={true}
                  className="w-full"
                />
                {balance && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1 p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                      <p className="text-muted-foreground font-medium">Plan Credits</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{balance.plan_credits.toLocaleString()}</p>
                    </div>
                    <div className="space-y-1 p-4 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                      <p className="text-muted-foreground font-medium">Purchased Credits</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {balance.purchased_credits.toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {usage?.month && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">This Month's Usage by Category</h4>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(usage.month.byCategory).map(([category, amount]) => (
                      <div key={category} className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg hover:shadow-sm transition-shadow duration-200">
                        <span className="text-sm capitalize text-muted-foreground font-medium">
                          {category.replace('_', ' ')}
                        </span>
                        <span className="font-bold text-gray-900 dark:text-gray-100">{amount.toLocaleString()} credits</span>
                      </div>
                    ))}
                    {Object.keys(usage.month.byCategory).length === 0 && (
                      <div className="text-center p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg">
                        <TrendingUp className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-muted-foreground">No usage this month</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
          <div className="h-2 bg-gradient-to-r from-emerald-200 via-green-200 to-teal-200 dark:from-emerald-800 dark:via-green-800 dark:to-teal-800" />
        </Card>
      </div>

      {/* Enhanced Tabs for History, Calculator, Structure, and Analytics */}
      <div className="px-4 sm:px-6 lg:px-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex justify-center">
            <TabsList className="grid w-full grid-cols-4 max-w-4xl bg-gradient-to-r from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border shadow-sm p-1">
              <TabsTrigger 
                value="history" 
                className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white transition-all duration-200"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">Transaction History</span>
                <span className="sm:hidden">History</span>
              </TabsTrigger>
              <TabsTrigger 
                value="calculator" 
                className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-600 data-[state=active]:text-white transition-all duration-200"
              >
                <Calculator className="w-4 h-4" />
                <span className="hidden sm:inline">Cost Calculator</span>
                <span className="sm:hidden">Calculator</span>
              </TabsTrigger>
              <TabsTrigger 
                value="structure" 
                className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-violet-600 data-[state=active]:text-white transition-all duration-200"
              >
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Credits Structure</span>
                <span className="sm:hidden">Structure</span>
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white transition-all duration-200"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Usage Analytics</span>
                <span className="sm:hidden">Analytics</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="history" className="space-y-4 mt-6">
            <CreditTransactionHistory />
          </TabsContent>

          <TabsContent value="calculator" className="space-y-4 mt-6">
            <CreditCostCalculator />
          </TabsContent>

          <TabsContent value="structure" className="space-y-4 mt-6">
            <CreditStructure />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4 mt-6">
            <CreditUsageAnalytics />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}